import type { LineItem, MappedTuple, Tuple } from "@/interfaces/estimate-detail";
import type { Estimation } from "@/interfaces/estimation.interface";
import { useShowDebugItems } from "@/hooks/useShowDebugItems";
import { getActiveAuditTrail, isItemArchived, LineItemDiffPath } from "@rebuild/shared";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sparkle, Wrench } from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import { Line } from "./Line";
import { LineItemsTabs } from "./Tabs/LineItemTabs";
import { SortableLine } from "./SortableLine";
import EngineSharedUtils from "./util/engine-shared-utils";
import { getConfidence } from "./util/get-confidence";
import { getMeasurementData } from "./util/get-measurement-data";
import { createMultiDragSortingStrategy } from "./util/multi-drag-sorting-strategy";
import { getCollapseShifts, setMultiDragOffset } from "./util/multi-drag-offset";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import { useDispatch, useSelector } from "@/redux/store";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { getKeyForMultiSelect } from "@/utils/utils";
import { SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { formatRuleCounts, getRuleLabelCountsFromAuditTrail } from "../../util/rule-labels";
import { useCallback, useMemo, useRef, useState } from "react";

export type Category = "issues" | "recommended" | "removeRules";

const NO_COLLAPSE_SHIFTS: ReadonlyMap<string, number> = new Map();

interface IconForCurrentItemProps {
  currentItem: LineItem;
}

const IconForCurrentItem = ({ currentItem }: IconForCurrentItemProps) => {
  const tooltipMessage = useMemo<string>(() => {
    const auditTrail = getActiveAuditTrail(currentItem.auditTrail);

    const countertopFormulaDiff = auditTrail.find(
      (diff) => diff.source === "convert_countertop_units" && diff.diff.path === LineItemDiffPath.Formula,
    );
    const countertopMessage = countertopFormulaDiff
      ? "Formula unit was auto-converted between LF and SF (countertop depth assumed to be 3 ft)."
      : "";

    const counts = getRuleLabelCountsFromAuditTrail(auditTrail, {
      pathsToLookAt: [LineItemDiffPath.LineItemId, LineItemDiffPath.Description, LineItemDiffPath.Formula],
    });
    const ruleMessage = formatRuleCounts("This line item was modified by", counts);

    return [countertopMessage, ruleMessage].filter(Boolean).join(" ");
  }, [currentItem.auditTrail]);

  if (currentItem.isComplement && !currentItem.addedByTPARule) {
    return <Sparkle className="h-5 w-5 text-blue-600" />;
  }

  if (tooltipMessage) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Wrench className="h-5 w-5 text-purple-600" />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
};

interface LinesProps {
  estimation?: Estimation;
  lines: MappedTuple[];
  tuples: Tuple[];
  estimateId?: string;
  itemCategoryTab: Record<Category, MappedTuple[]>;
  onSelect?: (uniqueId: string, category?: Category) => void;
  onDelete?: (val: string, uniqueId: string, category?: Category) => void;
  onReorder: (activeUuid: string, overUuid: string, draggedUuids?: Array<string>) => void;
  pageId: string;
}

export const Lines = ({ tuples, pageId, estimateId, lines, onDelete, onSelect, itemCategoryTab, estimation, onReorder }: LinesProps) => {
  const isDebugItemsEnabled = useShowDebugItems();
  const dispatch = useDispatch();
  const selectedLineItems = useSelector(estimationSelector.selectedLineItems);
  const [draggedUuids, setDraggedUuids] = useState<Array<string>>([]);
  const [collapseShifts, setCollapseShifts] = useState<ReadonlyMap<string, number>>(NO_COLLAPSE_SHIFTS);
  const listRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const getQuantityString = useCallback(
    (currentItem: LineItem) => {
      const quantityBeforeModifierFunc = (id: string, measurementType: string) => {
        return getMeasurementData(id, measurementType, estimation);
      };
      return EngineSharedUtils.getFinalQuantity(currentItem, pageId, quantityBeforeModifierFunc);
    },
    [estimation, pageId],
  );

  const visibleLines = useMemo(
    () => lines.filter(({ currentItem }) => isDebugItemsEnabled || !isItemArchived(currentItem)),
    [lines, isDebugItemsEnabled],
  );
  const sortableIds = useMemo(() => visibleLines.map(({ uuid }) => uuid), [visibleLines]);

  const sortingStrategy = useMemo(
    () => createMultiDragSortingStrategy(draggedUuids.map((uuid) => sortableIds.indexOf(uuid)).filter((index) => index !== -1)),
    [draggedUuids, sortableIds],
  );
  const draggedUuidSet = useMemo(() => new Set(draggedUuids), [draggedUuids]);

  const getSelectedUuids = () => {
    if (selectedLineItems.length === 0) return new Set<string>();
    const selectionKeys = new Set(selectedLineItems.map(getKeyForMultiSelect));
    return new Set(
      visibleLines
        .filter(({ tuple, currentItem }) =>
          selectionKeys.has(
            getKeyForMultiSelect({ tupleId: tuple.tupleId, lineItemId: currentItem.lineItemId, pageId, lineItem: currentItem.lineItem }),
          ),
        )
        .map(({ uuid }) => uuid),
    );
  };

  const setDragOffset = useCallback((x: number, y: number) => setMultiDragOffset(listRef.current, x, y), []);

  // Bail out when there's nothing to reset, so an ordinary single-row drag doesn't re-render the room.
  const endDrag = () => {
    setDraggedUuids((current) => (current.length ? [] : current));
    setCollapseShifts((current) => (current.size ? NO_COLLAPSE_SHIFTS : current));
    setDragOffset(0, 0);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeUuid = String(event.active.id);
    const selectedUuids = getSelectedUuids();

    // Grabbing a row that's part of a multi-selection drags the whole selection (in visual order);
    // grabbing anything else falls back to a single-row drag and drops the selection.
    if (selectedUuids.size > 1 && selectedUuids.has(activeUuid)) {
      const uuidsInDrag = visibleLines.filter(({ uuid }) => selectedUuids.has(uuid)).map(({ uuid }) => uuid);
      setDraggedUuids(uuidsInDrag);
      setCollapseShifts(getCollapseShifts(uuidsInDrag, sortableIds, activeUuid));
      return;
    }

    setDraggedUuids([]);
    dispatch(estimationActions.clearLineItemSelection());
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const movingUuids = draggedUuids;
    endDrag();

    if (!over) return;

    if (movingUuids.length > 1) {
      // the block was put back where it started.
      if (movingUuids.includes(String(over.id))) return;
      onReorder(String(active.id), String(over.id), movingUuids);
      dispatch(estimationActions.clearLineItemSelection());
      return;
    }

    if (active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  return (
    <Fragment>
      <LineItemsTabs
        estimateId={estimateId}
        estimation={estimation}
        tuples={tuples}
        pageId={pageId}
        onDelete={onDelete}
        onSelectItem={onSelect}
        itemCategoryTab={itemCategoryTab}
      />

      <div ref={listRef}>
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={endDrag}>
          <SortableContext items={sortableIds} strategy={sortingStrategy}>
            {visibleLines.map(({ uuid: currentUuid, tuple, currentItem }, index) => {
              const confidence = getConfidence(currentItem, tuple);
              const quantityString = getQuantityString(currentItem);
              const icon = <IconForCurrentItem currentItem={currentItem} />;

              // Add light red background for deleted items when debug mode is enabled
              const isDeletedItem = isItemArchived(tuple) || isItemArchived(currentItem);
              const additionalClassName = isDeletedItem && isDebugItemsEnabled ? "bg-red-50" : "";

              return (
                <SortableLine
                  key={`line_${currentUuid}`}
                  uuid={currentUuid}
                  isInDraggedBlock={draggedUuidSet.has(currentUuid)}
                  collapseShift={collapseShifts.get(currentUuid) ?? 0}
                  onDragOffsetChange={setDragOffset}
                >
                  {(dragHandle, isDragging) => (
                    <Line
                      isBeingMoved={isDragging}
                      showCheck
                      tuple={tuple}
                      order={index + 1}
                      isFreeFormFlag={tuple.isFreeFormFlag ?? false}
                      quantityString={quantityString}
                      estimateId={estimateId}
                      lineItem={currentItem}
                      className={`border-b ${additionalClassName}`}
                      icon={icon}
                      confidence={confidence}
                      pageId={pageId}
                      dragHandle={dragHandle}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(tuple.tupleId, currentItem.uniqueId);
                      }}
                      onSelect={() => onSelect?.(currentItem.uniqueId)}
                      tupleId={tuple.tupleId}
                    />
                  )}
                </SortableLine>
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
    </Fragment>
  );
};
